import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, User, Sparkles } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register(form.email, form.password, form.name);
      if (result?.verification_required) {
        // Stash fallback info in sessionStorage so VerifyEmailPage can show it
        // when email delivery failed. Avoids leaking through URL.
        if (result.manual_verification_code) {
          try {
            sessionStorage.setItem("kr_manual_code", JSON.stringify({
              email: form.email,
              code: result.manual_verification_code,
              discord: result.discord_founder_url || "",
              email_sent: !!result.email_sent,
              ts: Date.now(),
            }));
          } catch (_) { /* storage may be blocked */ }
        }
        if (result.email_sent) {
          toast.success("Kode OTP telah dikirim ke email kamu! Silakan cek Inbox / Spam.");
        } else {
          toast.info("Kode OTP dikirim ke email kamu. Gunakan juga backup code jika email lambat.");
        }
        nav(`/verify-email?email=${encodeURIComponent(form.email)}`);
      } else {
        toast.success("Welcome to the cult.");
        nav("/dashboard");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <PageShell noFooter>
      <div className="min-h-[80vh] grid place-items-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="font-mystic text-rose-400 text-sm">入信の儀</div>
            <h1 className="font-display text-3xl font-bold text-white mt-1">Initiation Rite</h1>
            <p className="text-zinc-400 text-sm mt-2">Create your account and join the dawn.</p>
          </div>
          <form onSubmit={submit} className="glass-strong rounded-xl p-7 space-y-5">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider">Cult Name</Label>
              <div className="relative mt-1.5">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  required
                  minLength={1}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="pl-10 bg-black/40 border-white/10 text-white"
                  data-testid="register-name-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="pl-10 bg-black/40 border-white/10 text-white"
                  data-testid="register-email-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pl-10 bg-black/40 border-white/10 text-white"
                  data-testid="register-password-input"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1.5">At least 6 characters.</p>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-rose-600 hover:bg-rose-500 text-white font-semibold py-6"
              data-testid="register-submit-btn"
            >
              {loading ? "Initiating…" : (<><Sparkles className="w-4 h-4 mr-2" /> Complete Initiation</>)}
            </Button>
            <div className="text-center text-sm text-zinc-400">
              Already a member?{" "}
              <Link to="/login" className="text-rose-400 hover:text-rose-300 font-semibold" data-testid="register-go-login">Sign in</Link>
            </div>
          </form>
        </div>
      </div>
    </PageShell>
  );
}
